// Generates a branded PDF of whichever market-finder table is currently shown,
// using jsPDF + autotable. The table is laid out to fit the page width (landscape).

// Montel wordmark (same paths as the site header logo). Rasterised to a canvas
// before being placed, since jsPDF can't draw raw SVG paths.
const LOGO_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="161" height="28" viewBox="0 0 161 28" fill="none">' +
  '<path d="M102.907 4.77053H94.7852V0.296875H116.238V4.77053H108.155V27.2677H102.92V4.77053H102.894H102.907Z" fill="#01796E"/>' +
  '<path d="M127.235 0.308594H138.542V4.79514H127.712C126.397 4.79514 125.34 5.85232 125.34 7.16734V11.5636H138.542V16.0502H125.34V20.4465C125.34 21.7615 126.397 22.8187 127.712 22.8187H138.542V27.2923H127.235C123.303 27.2923 120.118 24.1079 120.105 20.1757V7.46386C120.105 3.53169 123.277 0.334379 127.209 0.321486C127.209 0.321486 127.222 0.321486 127.235 0.321486V0.308594Z" fill="#01796E"/>' +
  '<path d="M143.093 0.308594H148.327V20.4336C148.327 21.7486 149.384 22.8058 150.699 22.8058H161V27.2794H150.21C146.277 27.2794 143.08 24.095 143.08 20.1629V0.308594H143.106H143.093Z" fill="#01796E"/>' +
  '<path d="M90.9556 7.43808V27.2666H85.7213V7.14155C85.7213 5.82653 84.6641 4.76936 83.3491 4.76936H73.654V27.2666H68.4326V0.308594H83.8261C87.7583 0.308594 90.9427 3.49301 90.9556 7.42519V7.43808Z" fill="#01796E"/>' +
  '<path d="M25.514 0.308594C29.4462 0.308594 32.6306 3.49301 32.6435 7.42519V27.2666H27.4092V7.14155C27.4092 5.82653 26.352 4.76936 25.037 4.76936H18.926V27.2666H13.6917V4.76936H7.6065C6.29148 4.76936 5.2343 5.82653 5.2343 7.14155V27.2666H0V7.43808C0 3.5059 3.18442 0.308594 7.11659 0.308594H25.514Z" fill="#01796E"/>' +
  '<path d="M50.538 5.0796C55.3469 5.0796 59.2404 8.97309 59.2404 13.7819C59.2404 18.5908 55.3469 22.4843 50.538 22.4843C45.7292 22.4843 41.8357 18.5908 41.8357 13.7819C41.8357 8.97309 45.742 5.0796 50.538 5.0796ZM50.538 0C42.9186 0 36.7432 6.17545 36.7432 13.7948C36.7432 21.4142 42.9186 27.5897 50.538 27.5897C58.1574 27.5897 64.3328 21.4142 64.3328 13.7948C64.3328 6.18834 58.1832 0.0128924 50.5638 0H50.538V0Z" fill="#01796E"/>' +
  "</svg>";

const LOGO_RATIO = 28 / 161; // height / width

class MarketFinderPDF {
  constructor() {
    const button = document.querySelector("#market-finder-download");
    if (!button) return;
    button.addEventListener("click", () => this.generatePDF());
  }

  generatePDF() {
    if (!window.jspdf) return;
    window.jsPDF = window.jspdf.jsPDF;

    const isMarkets = !document.querySelector("#panel-markets").hidden;
    const table = document.querySelector(isMarkets ? "#panel-markets table" : "#panel-commodities table");
    if (!table) return;

    const head = [[...table.querySelectorAll("thead th")].map((th) => th.childNodes[0].textContent.trim())];
    const body = [...table.querySelectorAll("tbody tr")].map((tr) =>
      [...tr.querySelectorAll("td")].map((td) => {
        const pills = td.querySelectorAll(".market-finder__pill");
        if (pills.length) {
          return [...pills].map((p) => p.textContent.trim()).join(", ");
        }
        return td.textContent.trim();
      })
    );

    const title = isMarkets ? "Markets" : "Commodities";

    // Rasterise the SVG logo first, then build the document.
    const img = new Image();
    img.onload = () => this.build({ head, body, title, logo: this.rasterise(img) });
    img.onerror = () => this.build({ head, body, title, logo: null });
    img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(LOGO_SVG);
  }

  rasterise(img) {
    const scale = 8; // render at 8x for a crisp placement
    const canvas = document.createElement("canvas");
    canvas.width = 161 * scale;
    canvas.height = 28 * scale;
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/png");
  }

  build({ head, body, title, logo }) {
    const doc = new jsPDF({ orientation: "landscape" });
    const left = 10;
    const logoWidth = 42;
    const logoHeight = logoWidth * LOGO_RATIO;

    doc.autoTable({
      head,
      body,
      startY: 32,
      theme: "striped",
      // Match the on-page table: dark header, light-green zebra rows, dark text.
      styles: {
        overflow: "linebreak",
        cellPadding: 3,
        fontSize: 9,
        textColor: [0, 41, 46], // --color-dark-10
        lineColor: [230, 234, 235], // --color-lightgrey
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [0, 46, 51], // --color-dark
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "left",
      },
      alternateRowStyles: { fillColor: [237, 251, 249] }, // --color-lightgreen-80
      columnStyles: { 0: { fontStyle: "bold" } }, // Country / Commodity
      margin: { left, right: 10 },
      didDrawPage: (data) => {
        if (data.pageNumber !== 1) return;
        const x = data.settings.margin.left; // align with the table's left edge
        if (logo) {
          doc.addImage(logo, "PNG", x, 8, logoWidth, logoHeight);
        }
        doc.setFontSize(15);
        doc.setTextColor(40);
        doc.text(`Montel — ${title}`, x, 26);
      },
    });

    doc.save(`montel-${title.toLowerCase()}.pdf`);
  }
}

export default MarketFinderPDF;
